import React, { useState } from 'react';
import { Bot } from 'lucide-react';
import { cn } from '../lib/utils';
import { IMAGES } from '../config/images';

const INLINE_FRAME = {
  sm: 'h-10 w-10 p-1',
  md: 'h-14 w-14 p-1.5',
  lg: 'h-20 w-20 p-2',
  xl: 'h-28 w-28 p-2.5',
  hero: 'h-36 w-36 p-3',
};

const INLINE_IMG = {
  sm: 'h-7 w-7',
  md: 'h-10 w-10',
  lg: 'h-14 w-14',
  xl: 'h-[5.5rem] w-[5.5rem]',
  hero: 'h-28 w-28',
};

/** Cutout sizes — larger so the mascot can overhang parents */
const CUTOUT_SIZE = {
  sm: 'h-14 w-14',
  md: 'h-20 w-20',
  lg: 'h-28 w-28 sm:h-32 sm:w-32',
  xl: 'h-36 w-36 sm:h-44 sm:w-44',
  hero: 'h-40 w-40 sm:h-52 sm:w-52 md:h-60 md:w-60',
};

const PEEK_SIZE = {
  sm: 'h-16 w-16',
  md: 'h-20 w-20 sm:h-24 sm:w-24',
  lg: 'h-24 w-24 sm:h-28 sm:w-28',
  xl: 'h-28 w-28 sm:h-32 sm:w-32',
  hero: 'h-36 w-36',
};

/**
 * DigiRobot mascot — PNG transparent cutout.
 * - inline: optional framed badge
 * - popout: absolute overhang (3D pop-out) — parent must be overflow-visible
 * - peek: larger unframed cutout that can peek past a column
 */
const RobotMascot = ({
  className,
  size = 'md',
  variant = 'inline',
  framed,
  src = IMAGES.robotMascot,
}) => {
  const [waving, setWaving] = useState(false);
  const isPopout = variant === 'popout';
  const isPeek = variant === 'peek';
  const isCutout = isPopout || isPeek;
  const isFramed = framed ?? (!isCutout);

  const imgClass = isPopout
    ? CUTOUT_SIZE[size] || CUTOUT_SIZE.md
    : isPeek
      ? PEEK_SIZE[size] || PEEK_SIZE.md
      : INLINE_IMG[size] || INLINE_IMG.md;

  return (
    <div
      className={cn(
        'robot-mascot select-none',
        isPopout && [
          'absolute z-10 overflow-visible pointer-events-auto',
          'bottom-0 right-0 translate-x-[14%] translate-y-[22%]',
          'drop-shadow-[0_18px_32px_rgba(24,68,158,0.35)]',
        ],
        isPeek && [
          'relative inline-flex overflow-visible',
          'translate-y-3',
          'drop-shadow-[0_10px_20px_rgba(24,68,158,0.22)]',
        ],
        !isCutout && 'inline-flex items-center justify-center',
        isFramed && [
          'rounded-xl cursor-default',
          'bg-gradient-to-br from-[rgba(var(--brand-primary-rgb),0.12)] to-[rgba(var(--brand-accent-rgb),0.14)]',
          'border border-[rgba(var(--brand-primary-rgb),0.28)]',
          'transition-all duration-300 hover:border-[rgba(var(--brand-accent-rgb),0.45)] hover:shadow-lg hover:shadow-[0_10px_28px_-12px_rgba(var(--brand-primary-rgb),0.35)]',
          'beep-hover',
          INLINE_FRAME[size] || INLINE_FRAME.md,
        ],
        !isFramed && 'bg-transparent border-0',
        className
      )}
      onMouseEnter={() => setWaving(true)}
      onMouseLeave={() => setWaving(false)}
      role="img"
      aria-label="Mascotte robot DigiRobot"
    >
      {src ? (
        <img
          src={src}
          alt=""
          className={cn(
            imgClass,
            'object-contain object-bottom animate-robot-float transition-transform duration-300',
            waving && 'robot-mascot-wave'
          )}
          draggable={false}
        />
      ) : (
        <Bot
          className={cn(
            imgClass,
            'text-[color:var(--brand-primary-bright)] animate-robot-float transition-transform duration-300',
            waving && 'robot-mascot-wave'
          )}
        />
      )}
    </div>
  );
};

export default RobotMascot;
